'use client';

import type { IconButtonProps } from '@chakra-ui/react';
import { ClientOnly, IconButton, Skeleton } from '@chakra-ui/react';
import { Moon, Sun } from 'lucide-react';
import { ThemeProvider, useTheme } from 'next-themes';
import type { ThemeProviderProps } from 'next-themes/dist/types';
import { forwardRef } from 'react';

export function ColorModeProvider(props: ThemeProviderProps) {
    return <ThemeProvider attribute="class" disableTransitionOnChange {...props} />;
}

export function useColorMode() {
    const { resolvedTheme, setTheme } = useTheme();
    const toggleColorMode = () => {
        setTheme(resolvedTheme === 'light' ? 'dark' : 'light');
    };
    return {
        colorMode: 'light',
        setColorMode: setTheme,
        toggleColorMode,
    };
}

export function useColorModeValue<T>(light: T, dark: T) {
    const { colorMode } = useColorMode();
    return colorMode === 'light' ? light : dark;
}

export function ColorModeIcon() {
    const { colorMode } = useColorMode();
    return colorMode === 'light' ? <Sun /> : <Moon />;
}

interface ColorModeButtonProps extends Omit<IconButtonProps, 'aria-label'> {}

export const ColorModeButton = forwardRef<HTMLButtonElement, ColorModeButtonProps>(function ColorModeButton(props, ref) {
    const { toggleColorMode } = useColorMode();
    return (
        <ClientOnly fallback={<Skeleton boxSize="8" />}>
            <IconButton
                onClick={toggleColorMode}
                variant="ghost"
                aria-label="Toggle color mode"
                size="sm"
                ref={ref}
                {...props}
                css={{
                    _icon: {
                        width: '5',
                        height: '5',
                    },
                }}>
                <ColorModeIcon />
            </IconButton>
        </ClientOnly>
    );
});
